import React, { useEffect, useRef, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
//mui
import { Dialog, DialogContent, Box, IconButton, Stepper, Step, StepLabel, Button } from '@mui/material';
import { styled } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
//components
import CircularProgress from '../../../../../components/loaders/CircularLoader';
import CustomButton from '../../../../../components/buttons/CustomButton';
import ImageDisplay from '../ImageViewerModal/components/ImageInspector/ImageDisplay';
import Step1SelectFrame from './components/Step1SelectFrame';
import Step2MarkFrame from './components/Step2MarkFrame';
import Step3DescribeItem from './components/Step3DescribeItem';
//slice
import { handleCloseARIModal, onNewReportItem, setRectARI, setSelectedImageIndex } from '../../../../../store/slices/reportsSlice';
//utils
import { postReqOptBuilder } from '../../../../../utils/main_utils';
import { para_be } from '../../../../../config';


/*

Modal that will allow end users to add a report item

The end user will experience the following:
- To choose a frame. If they select add new item from a certain room, we should limit the frames to that room.
- Once a frame has been selected, the screen will change, it will show the selected frame and a guide on marking the area of interest in the frame.
- If the end user is not interested in selecting an area of interest, they can skip this step, and we will mark the whole frame as the area of interest.
- Once an area of interest has been selected, the end user will be able to enter a title, description, and assign it to a category in the room.

This means we need to have the following screens:
Maybe we should have at the top like a wizard stepper, that will show the steps the end user is in and how many are left.
1. We show a screen: On the left we will show the selected frame in large, 
    while on the right we will show a description of the process and list of frames (frame selection view).
    They will have buttons at the bottom to cancel or continue in the process.
2. On the left we show the same selected frame, but on the right we show a description of this step and a guide on how to select the area of interest.
    Additionally, we will have coordinates inputs in case they want to update the coordinates manually.
    They will have buttons at the bottom to go back, continue, or skip the step.
3. On the left we show the same selected frame, with the area of interest marked,
    while on the right we show input fields for the title, description, and category.
    They will have buttons at the bottom to go back or Save the new item.

- Once the end user has pressed save, we will show the green notification that the item has been added successfully. and will show immediately the item view.

*/

const steps = ['Select Frame', 'Mark Area of Interest', 'Describe Item'];


const CustomStepLabel = styled(StepLabel)({
    '& .MuiStepLabel-label': {
        fontSize: '0.7rem',
    },
});


const AddReportItemModal = ({ setNotifState }) => {
    const dispatch = useDispatch();
    const { report_id, inspectionMediaData, addReportItemModal, roomsData, orderedData, tabView } = useSelector((state) => state.reports);
    const isMobile = useSelector((state) => state.config?.isMobile);
    
    const [loading, setLoading] = useState(false);
    const [loadingModal, setLoadingModal] = useState(false);
    const [activeStep, setActiveStep] = useState(0);
    const [allFrames, setAllFrames] = useState([]);
    const [roomFrames, setRoomFrames] = useState([]);
    const [roomCategories, setRoomCategories] = useState([]);
    
    const rectDrawCoords = useRef(null);
    const isDrawing = useRef(false);
    const imgRef = useRef();
    const imgContainerRef = useRef();
    const rectRef = useRef();
    const step1Ref = useRef();
    const step2Ref = useRef();
    const step3Ref = useRef();


    useEffect(() => {
        if (addReportItemModal) {
            setLoadingModal(true);
            const selectedRoom = addReportItemModal?.room;
            const selectedRoomFrames = roomsData.reduce((acc, room) => [
                ...acc, 
                ...(selectedRoom === room.title ? Array.from({ length: (Math.floor(room.end_frame) - Math.floor(room.start_frame)) }, (_, i) => i + Math.floor(room.start_frame)) : [])
            ], []);
            const frameNumbers = inspectionMediaData?.frame_numbers.map((frame, idx) => [Number(frame), idx]);
            const framesDict = frameNumbers.reduce((acc, [frame, idx]) => ({ 
                ...acc, 
                ["https://aiv2.paraspot.ai/cva/presentImage/UniqueFrames_" + inspectionMediaData.ts + "?idx=" + idx + "_orig_size&lt=1"]: [idx, frame] 
            }), {});
            setAllFrames(framesDict);
            const targetFrames = frameNumbers.filter(
                ([frame]) => selectedRoomFrames.includes(frame)
            ).map(([, idx]) => "https://aiv2.paraspot.ai/cva/presentImage/UniqueFrames_" + inspectionMediaData.ts + "?idx=" + idx + "_orig_size&lt=1");
            setRoomFrames(targetFrames);

            const roomCategories = [...new Set(orderedData[tabView].filter((item) => item.room === selectedRoom).map((item) => item?.classifiedCategory))];
            setRoomCategories(roomCategories);

            setLoadingModal(false);
        }
    }, [addReportItemModal]);


    const getStepContent = stepIndex => {
        switch (stepIndex) {
            case 0:
                return <Step1SelectFrame ref={step1Ref} allFrames={allFrames} roomFrames={roomFrames} isMobile={isMobile} />;
            case 1:
                return <Step2MarkFrame 
                            ref={step2Ref} 
                            isMobile={isMobile} 
                            rectDrawCoords={rectDrawCoords} 
                            isDrawing={isDrawing} 
                            imgRef={imgRef} 
                            imgContainerRef={imgContainerRef} 
                            rectRef={rectRef} />;
            case 2:
                return <Step3DescribeItem 
                            ref={step3Ref} 
                            report_id={report_id}
                            itemData={addReportItemModal} 
                            roomCategories={roomCategories} 
                            setNotifState={setNotifState} />;
            default:
                return 'Unknown Step';
        }
    };

    const handleBack = () => {
        setActiveStep(prev => prev - 1);
    };

    const handleNext = () => {
        if (activeStep === 0) {
            if (!addReportItemModal?.selectedFrame) {
                setNotifState({
                    text: "Please select a frame",
                    type: "error",
                });
                return;
            }
        } else if (activeStep === 1) {
            if (!addReportItemModal?.rect) {
                dispatch(setRectARI({rect: {x: 0, y: 0, width: 720, height: 1280}}));
            }
        } else {
            setLoading(true);
            step3Ref.current.handleSubmit()
            .then((payload) => {
                if (payload) {
                    fetch(para_be + "/reports/create_new_item", postReqOptBuilder(payload))
                    .then((response) => response.json())
                    .then((response) => {
                        if (response.status === 200) {
                            setNotifState({
                                text: "Item added successfully",
                                type: "success",
                            });
                            const responseObject = Object.entries(response.result)[0];
                            dispatch(onNewReportItem({object_id: responseObject[0], object_data: responseObject[1]}));
                            setLoading(false);
                            dispatch(handleCloseARIModal());
                        } else {
                            setNotifState({
                                text: response.msg,
                                type: "error",
                            });
                            setLoading(false);
                        }
                    })
                    .catch((error) => {
                        console.error("Error:", error);
                        setNotifState({
                            text: "Something went wrong.",
                            type: "error",
                        });
                        setLoading(false);
                    })
                } else {
                    setLoading(false);
                }
            });
        }
        if (activeStep !== 2) {
            setActiveStep(prev => prev + 1);
        }
    };


    return (
        <Dialog
            open={addReportItemModal !== null}
            onClose={() => dispatch(handleCloseARIModal())}
            // disableScrollLock
            fullScreen
            slotProps={{
                container: {
                    sx: {
                        height: 'auto', // Set this to 'auto' or any specific value you prefer
                    },
                },
            }}
            PaperProps={{
                sx: {
                    width: '60v',
                    maxWidth: isMobile ? '100vw' : '1000px',
                    height: '80vh',
                    borderRadius: '10px',
                    overflow: 'hidden',
                    backgroundColor: 'rgba(255, 255, 255)',
                    boxShadow: 24,
                    position: 'relative',
                },
            }}
        >
            <IconButton
                onClick={() => dispatch(handleCloseARIModal())}
                sx={{
                    position: 'absolute',
                    top: 8,
                    right: 8,
                    zIndex: 2,
                    color: 'black',
                }}
            >
                <CloseIcon />
            </IconButton>
            
            <DialogContent
                sx={{
                    paddingX: 2,
                    paddingY: 8,
                    display: 'flex',
                    flexDirection: 'column',
                    backgroundColor: 'transparent',
                    height: '100%',
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        flex: 1,
                        justifyContent: 'center',
                        position: 'relative',
                        width: '100%',
                        height: '100%',
                    }}
                >
                    {loadingModal ?
                        <CircularProgress size={50} /> :
                        <div className="flex">
                            <Box 
                                sx={{ 
                                    display: 'flex', 
                                    minWidth: '400px',
                                }}
                            >
                                <Box 
                                    sx={{ 
                                        display: 'flex', 
                                        width: '100%' 
                                    }}
                                >
                                    <ImageDisplay
                                        imgId="addItem-Frame"
                                        imgRef={imgRef}
                                        containerRef={imgContainerRef}
                                        rectRef={rectRef}
                                        title={"Selected Frame"}
                                        src={addReportItemModal?.selectedFrame || roomFrames?.[0]}
                                        alt={""}
                                        isMobile={isMobile}
                                        showTitle={false}
                                    />
                                </Box>
                            </Box>

                            <div className="flex flex-col relative overflow-x-hidden overflow-y-auto custom-scrollbar-1" style={{ minWidth: '300px' }}>
                                <Stepper activeStep={activeStep} alternativeLabel>
                                    {steps.map((label, index) => (
                                        <Step key={label}>
                                            <CustomStepLabel>{label}</CustomStepLabel>
                                        </Step>
                                    ))}
                                </Stepper>

                                <div>
                                    {getStepContent(activeStep)}
                                    
                                </div>
                            </div>
                        </div>
                    }

                </Box>
                {!loadingModal &&
                    <div className="flex gap-4 justify-end absolute right-0 p-3 pr-12 bg-white" style={{ bottom: "0" }}>
                        <Button disabled={activeStep !== 1 || loadingModal || loading} onClick={handleNext}>
                            Skip
                        </Button>
                        
                        <Button 
                            sx={{
                                backgroundColor: 'transparent', 
                                color: 'rgb(25, 118, 210)',
                                '&:hover': {
                                    backgroundColor: 'transparent', 
                                    color: 'rgb(25, 118, 210)',
                                }
                            }}
                            variant="contained" 
                            disabled={activeStep === 0 || loadingModal || loading} 
                            onClick={handleBack}
                        >
                            Back
                        </Button>

                        <CustomButton variant="contained" color="primary" loaderColor="white" onClick={handleNext} disabled={loadingModal || loading} loading={loading}>
                            {activeStep === 2 ? 'Finish' : 'Next'}
                        </CustomButton>
                    </div>
                }
            </DialogContent>
        </Dialog>
    );
};

export default AddReportItemModal;